import { FORM_TEXTS } from '@jotforminc/constants';
import EmojiPicker from './EmojiPicker';
import { isValidUrl } from './validationHelper';
import {
  forEach,
  findIndex,
  addClass,
  hasClass,
  htmlToText,
  toggleClass,
  removeClass,
  getSelectedRange,
  setSelectionRange,
  isDescendant
} from './utils';

class Textarea {
  constructor({ cardContainer, fieldContainer, question: { id, qid } }, browserName, osName) {
    // Question properties
    this.questionID = id;

    // OS & Browser name to change couple handling
    this.osName = osName;
    this.browserName = browserName;

    // Command/tag map for marking active the toolbar buttons
    this.commandTagMap = {
      bold: ['B', 'STRONG'],
      italic: ['I', 'EM'],
      underline: ['U'],
      insertOrderedList: ['OL'],
      insertUnorderedList: ['UL'],
      formatBlock: ['BLOCKQUOTE'],
      createLink: ['A'],
      insertImage: ['IMG'],
      heading: ['H1', 'H2', 'H3', 'H4', 'H5', 'H6']
    };

    // Keys of command/tag map are the editor buttons that need to be updated in toolbar
    this.togglableCommands = Object.keys(this.commandTagMap);

    // Key/command mapping for shortcuts
    this.keyCommandMap = {
      k: 'createLink',
      b: 'bold',
      i: 'italic',
      u: 'underline'
    };

    // Shortcut key to check on events for detecting command shortcuts
    this.shortcutKey = this.osName === 'macOS' ? 'metaKey' : 'ctrlKey';

    // Flag for preventing meta key listening on shortcut usage
    this.preventNextKeyUp = false;

    // Events to listen for syncing input and markdown editor
    this.editorManipulationEventList = ['blur', 'input', 'keyup', 'keydown', 'paste', 'copy', 'cut', 'delete', 'mouseup', 'textinput'];

    // These events either does not supported or trigger constantly by iOS Safari. Therefore we remove them from iOS
    if (this.osName !== 'iOS') {
      this.editorManipulationEventList = this.editorManipulationEventList.concat(['selectionchange', 'DOMNodeInserted', 'DOMNodeRemoved']);
    } else { // iOS safari does not support selectionchange events so we trace end of touch event
      this.editorManipulationEventList = this.editorManipulationEventList.concat(['touchend']);
    }

    // Render type will be decided over rendered property
    this.renderType = fieldContainer.getAttribute(['data-render-type']);

    // Main input element of textarea card (Can be hidden or textarea)
    this.editorInput = cardContainer.querySelector(`#${qid}`);

    // Limit element of editor
    this.limitSpan = cardContainer.querySelector(`#${qid}-limit`);

    // Existance of limit element shows that editor has entry limit validation
    if (this.limitSpan) {
      // Get limit validation parameters and set them to object
      this.maxLimit = this.limitSpan.getAttribute('data-limit');
      this.minLimit = this.limitSpan.getAttribute('data-minimum');
      this.limitType = this.limitSpan.getAttribute('data-limit-type');
      this.limitSpan.textContent = `0/${this.minLimit > 0 ? this.minLimit : this.maxLimit}`;
    }

    // Selection temp variable to store editor selection on modal events
    this.selectionState = null;

    // Bindings
    this.initEditor = this.initEditor.bind(this);
    this.saveSelection = this.saveSelection.bind(this);
    this.getActiveTags = this.getActiveTags.bind(this);
    this.limitValidation = this.limitValidation.bind(this);
    this.isCommandActive = this.isCommandActive.bind(this);
    this.restoreSelection = this.restoreSelection.bind(this);
    this.bindEditorButton = this.bindEditorButton.bind(this);
    this.handleKeydownEvent = this.handleKeydownEvent.bind(this);
    this.setHtmlFromMarkdown = this.setHtmlFromMarkdown.bind(this);
    this.updateToolbarStatus = this.updateToolbarStatus.bind(this);
    this.setMarkdownFromHtml = this.setMarkdownFromHtml.bind(this);
    this.setHtmlFromMarkdown = this.setHtmlFromMarkdown.bind(this);
    this.handleEditorMutation = this.handleEditorMutation.bind(this);
    this.bindEditorButtonModal = this.bindEditorButtonModal.bind(this);
    this.bindEditorButtonSelect = this.bindEditorButtonSelect.bind(this);
    this.handleEditorButtonClick = this.handleEditorButtonClick.bind(this);
    this.getToolbarButtonByCommand = this.getToolbarButtonByCommand.bind(this);

    if (this.renderType === 'mde') {
      // Editor markup elements
      this.editorClassName = `inlineEditor-${id}`;
      this.editorToolbar = cardContainer.querySelector(`#${qid}_toolbar`);
      this.editorContainer = cardContainer.querySelector(`#${qid}_editor`);
      this.editorToolbarButtons = this.editorToolbar ? this.editorToolbar.getElementsByClassName('jfToolbar-item') : {};

      // Porting limit span for fullscreen mode
      if (this.limitSpan && this.editorToolbar) {
        this.editorToolbar.appendChild(this.limitSpan.parentNode);
        this.editorInput.limitValidation = this.limitValidation;
      }

      if (this.editorContainer && this.editorToolbar) {
        // Initialize markdown editor
        this.initEditor();
      }
    }
  }

  // Native selection object
  get selection() {
    return global.getSelection ? global.getSelection() : {};
  }

  // Selection collapsed means nothing selected at the moment; cursor just placed at somewhere
  get isSelectionCollapsed() {
    return this.selection ? this.selection.isCollapsed : false;
  }

  // Dom element that contains cursor currently
  get selectionFocus() {
    return this.selection ? this.selection.focusNode : {};
  }

  // Stores selection state
  saveSelection() {
    this.selectionState = getSelectedRange();
  }

  // Restores stored selection state and clear current selection
  restoreSelection() {
    // Set browser selection to the stored state
    setSelectionRange(this.selectionState);

    // Clear stored selection
    this.selectionState = null;
  }

  // Get editor toolbar buttons via command names
  getToolbarButtonByCommand(command) {
    return this.editorToolbarButtons.namedItem(`input_${this.questionID}_${command}`);
  }

  // Initialize method of basic textarea
  initBasic() {
    // Event bindings for textarea limit validation
    forEach(this.editorManipulationEventList, eventType => this.editorInput.addEventListener(eventType, () => this.limitsCounter()));
  }

  // Editor toolbar button bindind method
  bindEditorButton(btn) {
    const command = btn.getAttribute('data-command');
    btn.addEventListener('keypress', e => { if (e.keyCode === 32) e.target.click(); });
    switch (command) {
      case 'createLink':
      case 'insertImage':
        this.bindEditorButtonModal(btn, command);
        break;
      case 'insertEmoji':
        this.emojiPicker = new EmojiPicker(btn, this.editorContainer, this.restoreSelection);
        break;
      case 'heading':
        // Heading is a special case due to dropdown; therefore we are redirecting the binding method
        this.bindEditorButtonSelect(btn, command);
        break;
      default:
        btn.addEventListener('click', () => this.handleEditorButtonClick(btn, command));
        break;
    }
  }

  // Editor toolbar binding button (Specifically that contains select inside itself)
  bindEditorButtonSelect(btn, command) {
    const listParent = btn.querySelector('ul');
    const listItems = listParent.getElementsByTagName('li');

    // If ul list did not get rendered, then we need to hide btn as it will not function properly
    if (!listParent) {
      return btn.hide();
    }

    // Handle list item selections
    btn.addEventListener('click', evt => {
      // Stop browser events to prevent focusing toolbar items
      evt.stopPropagation();

      const { target } = evt;

      switch (target.tagName) {
        case 'DIV':
          // Handle show/hide of dropdown
          toggleClass(listParent, 'isVisible');
          target.setAttribute('aria-expanded', hasClass(listParent, 'isVisible'));

          // Restore selection to not mislead user
          this.restoreSelection();
          if (['Firefox', 'Safari'].indexOf(this.browserName) > -1) {
            // Firefox handles multiple selection so restoring does not malfunction the process
            this.saveSelection();
          }
          listParent.children[0].focus();
          break;
        default:
          // Handle click event like a regular editor button for list item
          const value = target.getAttribute('data-value');
          this.handleEditorButtonClick(target, command, value);

          // Clear selection of list items
          forEach(listItems, el => el.setAttribute('data-selected', false));
          forEach(listItems, el => el.setAttribute('aria-checked', false));

          // Mark as selected
          target.setAttribute('data-selected', true);
          target.setAttribute('aria-checked', true);

          // Hide dropdown
          removeClass(listParent, 'isVisible');
          break;
      }
    });

    document.body.addEventListener('click', ({ target }) => {
      // Do not handle this one
      if (target === btn || isDescendant(btn, target)) return;

      removeClass(listParent, 'isVisible');
    });
  }

  bindEditorButtonModal(btn, command) {
    const modalDiv = btn.getElementsByClassName('jfToolbar-modal')[0];
    const modalBtn = btn.getElementsByClassName('modal-btn')[0];
    const modalInput = btn.getElementsByTagName('input')[0];

    // If one of them is undefined then we need to hide this btn as it will not function properly
    if (!modalDiv || !modalInput || !modalBtn) {
      return btn.hide();
    }

    const handleModalExecution = () => {
      // Trigger command execution
      this.handleEditorButtonClick(btn, command, modalInput.value);

      // Clear input and hide modal at the end
      modalInput.value = '';
      removeClass(modalDiv, 'isVisible');
    };

    // Handle editor button click; if command is active, trigger click handle function to remove it; otherwise just toggle visibility
    btn.addEventListener('click', ({ target }) => {
      // Make sure bindend button is focused
      if (target !== btn) {
        return;
      }
      // Restore selection to make accurate command state detection
      this.restoreSelection();

      // Store the command state in order to store selection state again
      const isCommandActive = this.isCommandActive(command);

      // Save seletion state again
      this.saveSelection();

      // If command is active, just undo it from selection
      if (isCommandActive) {
        this.handleEditorButtonClick(btn, command);
      } else {
        // Get selected text via focused node
        const selectedText = this.selectionFocus.textContent;

        // For image and link, if selected text is a valid url then execute command without opening modal
        if (!this.isSelectionCollapsed && ['insertImage', 'createLink'].indexOf(command) !== -1 && isValidUrl(selectedText)) {
          return this.handleEditorButtonClick(btn, command, selectedText);
        }

        // Otherwise just toggle visibility
        toggleClass(modalDiv, 'isVisible');

        // If modal just opened, directly focus the modal input in order to handle modal close action on its blur event
        if (hasClass(modalDiv, 'isVisible')) {
          modalInput.focus();
        }
      }
    });

    // Hide modals on input and button blur
    modalInput.addEventListener('blur', () => {
      // Intention of delay is to capture selection correctly
      setTimeout(() => {
        const focusNode = this.selectionFocus;

        // No need to trigger remove process on modal div based elements. It will be handled by other bindings
        if (focusNode !== modalDiv && focusNode.parentNode !== modalBtn && focusNode.parentNode !== btn) {
          removeClass(modalDiv, 'isVisible');
        }
      }, 0);
    });

    // Complete command execution on enter keydown
    modalInput.addEventListener('keydown', e => {
      if (e.keyCode === 13) {
        // Prevent form submit and similar events
        e.preventDefault();

        // Trigger execcommand and hide modal
        handleModalExecution();
      }
    });

    modalBtn.addEventListener('click', handleModalExecution);
    modalBtn.addEventListener('keypress', e => {
      if (e.keyCode === 13 || e.keyCode === 32) {
        e.preventDefault();
        e.stopPropagation();
        modalBtn.click();
      }
    });
  }

  handleEditorButtonClick(btn, command, value) {
    // Restore selection before continuing
    this.restoreSelection();

    // Decide what to do based on command
    switch (command) {
      case 'formatBlock':
        // If blockquote is already active, we will use div tag to clear it; otherwise add blockquote
        const tagToAdd = hasClass(btn, 'active') ? '<DIV>' : '<BLOCKQUOTE>';
        document.execCommand('formatBlock', false, tagToAdd);
        break;
      case 'heading':
        document.execCommand('formatBlock', false, `H${value}`);
        break;
      case 'createLink':
        const isLinkActive = this.isCommandActive('createLink');
        if (isLinkActive) {
          document.execCommand('unlink');
          break;
        } else if (this.browserName === 'Firefox') {
          document.execCommand('insertHTML', false, `<a href="${value}" target="_blank">${value}</a>`);
          break;
        }
        // fall through if link does not exist in the selection
      default:
        document.execCommand(command, false, value);

        /*
        * 1. Do not update unnecessary buttons like insertHorizontalRule
        * 2. Toggle activity class if nothing selected; otherwise it will be detected by updateToolbarStatus method
        * and it will try to override the correct status.
        */
        if (this.togglableCommands.indexOf(command) !== -1 && this.isSelectionCollapsed) {
          // Remove or add class based on command status
          toggleClass(btn, 'active');
          btn.setAttribute('aria-pressed', hasClass(btn, 'active'));
        }
        break;
    }
  }

  handleEditorMutation(event, delayedCall) {
    const { type } = event;
    const { osName, browserName, editorInput } = this;

    // Delay touchend trigger as iOS selection api works inconsistently
    if (osName === 'iOS' && !delayedCall && ['touchend']) {
      setTimeout(() => this.handleEditorMutation(event, true), 125); // Magic number
    }

    // Update value of real input
    editorInput.value = this.editorContainer.textContent;

    // Mirror the event trigger on editor input for binded methods (Condition/calculation check etc.)
    editorInput.dispatchEvent(global.createNewEvent(type));

    // Couple events causes malfunctions on browsers like Safari and Firefox. Therefore, some of the functions will be called using this check
    const isSaveNeed = ['blur'].indexOf(type) === -1 || browserName === 'Chrome';
    const isUpdateNeed = ['blur', 'keyup'].indexOf(type) === -1 || (browserName === 'Internet Explorer' && type === 'blur') || browserName === 'Chrome';

    if (isSaveNeed) {
      // Save current selection due to the fact that clicking or focusing another may cause losing this state
      this.saveSelection();
    }

    switch (type) {
      case 'keydown':
        this.handleKeydownEvent(event);
        break;
      case 'keyup':
        // Prevent further action if meta key marked to prevent next trigger
        if (this.preventNextKeyUp) {
          this.preventNextKeyUp = false;
          break;
        }
        // fall through to update status bar
      default:
        if (isUpdateNeed) {
          this.updateToolbarStatus();
        }
        break;
    }

    // Update limit status if it is defined
    if (this.limitSpan) {
      this.limitValidation();
    }
  }

  handleKeydownEvent(event) {
    // This one goes to the one I love.
    // Event is related to a editor shortcut like making text bold/italic etc.
    if (event[this.shortcutKey] === true && this.keyCommandMap[event.key]) {
      // Prevent event as we already that the indended action
      event.preventDefault();

      // Matching command with key
      const command = this.keyCommandMap[event.key];
      // Get btn of that command
      const btn = this.getToolbarButtonByCommand(command);
      // Simulate a click for execCommand and activity status update
      this.handleEditorButtonClick(btn, command);

      // If nothing selected at the moment
      if (this.isSelectionCollapsed) {
        // Prevent next meta key up for not malfunctioning toolbar state
        this.preventNextKeyUp = true;
      }
    } else if (event.shiftKey === true && event.key === 'Tab') {
      // Event is intended to do a outdent
      document.execCommand('outdent');
      event.preventDefault();
    } else if (event.key === 'Tab') {
      // Event is intended to do a indent
      document.execCommand('indent');

      event.preventDefault();
    } else if (event.key === 'Escape') {
      event.target.blur();
    }
  }

  // Parse active tags from parsing given input
  getActiveTags(input) {
    if (!input) return {};

    if (input.parentNode === this.editorContainer) {
      const { tagName } = input;

      // Use font size as object value to pass necessary functions the font size information; otherwise just mark it as true (dummy value)
      return { [tagName]: this.commandTagMap.heading.indexOf(tagName) > -1 ? input.getAttribute('size') : true };
    }

    return { ...this.getActiveTags(input.parentNode), [input.tagName]: 1 };
  }

  isCommandActive(command) {
    // Get matching tag
    const tagsToSearch = this.commandTagMap[command];

    // If there is not any matching tag with given command
    if (!tagsToSearch) return false;

    // Get active tags via selected node
    const activeTags = Object.keys(this.getActiveTags(this.selectionFocus));

    return activeTags.findIndex(tag => tagsToSearch.indexOf(tag) > -1) > -1;
  }

  updateToolbarStatus() {
    // Currently selected dom node
    const focusNode = this.selectionFocus;

    /*
    * In short: Do not remove this check.
    *
    * If the currently selected node is the editor container itself, it is best to not update the toolbar.
    * Main problem in here is that if one remove all of the line while 'bold' or any similar exec command exist,
    * browser will keep bold command in active state; although there is not any text on the line.
    * Due to the fact we are trying to detect state of buttons via tags in the line, 'getActiveTags' will not able to find required tag (Yet, the command is still active)
    * and toolbar status update will malfunction. The indicator of this situation is the focusNode of selection becomes editorContainer itself.
    *
    */
    if (focusNode === this.editorContainer) return;

    // Parse selection to get wrapper tags
    const activeTagsInfo = this.getActiveTags(focusNode);
    const activeTags = Object.keys(activeTagsInfo);

    // Check button commands to mark appropriate ones as active
    forEach(this.editorToolbarButtons, btn => {
      // Get matching tag with toolbar button command
      const tagsToSearch = this.commandTagMap[btn.getAttribute('data-command')];

      // If command does not have matching tag skip it
      if (tagsToSearch === undefined) return;

      // Get editor button list items
      const listItems = btn.getElementsByTagName('li');

      // Get index if tag from list
      const index = findIndex(activeTags, tagsToSearch);

      // If list items exist, we need to handle their state instead of button
      if (listItems && listItems.length > 0) {
        // Clear list items and mark one of them as active
        forEach(listItems, li => {
          const value = li.getAttribute('data-value');
          const tag = activeTags[index];
          const tagValue = activeTagsInfo[tag];

          // Do not process tag or non strings
          if (tag === 'undefined' || typeof tagValue !== 'string') return;

          // If value matches with the list item, mark it as active; otherwise toggle it to passive
          li.setAttribute('data-selected', index !== -1 && value === tagValue.replace(/^\D+/g, ''));
        });
      } else {
        // Decide with method to use: If tag exists use add method; otherwise use remove method
        const methodToUse = index !== -1 ? addClass : removeClass;

        // Update classlist of button
        methodToUse.call(this, btn, 'active');
        btn.setAttribute('aria-pressed', hasClass(btn, 'active'));
      }
    });
  }

  limitValidation() {
    // Initialize text to check for limits
    const textToCheck = this.renderType === 'mde' ? htmlToText(this.editorContainer) : this.editorInput.value;

    // Get count based on counting type
    const count = this.limitType === 'Words' ? textToCheck.split(/[\n\r\s]+/).length : textToCheck.length;

    // Check limitation status
    const isMinFailed = this.minLimit > 0 && count < this.minLimit;
    const isMaxFailed = this.maxLimit > 0 && count > this.maxLimit;

    // Decide which text to use as max limit
    const limitValue = (isMinFailed && !isMaxFailed) || (this.minLimit > 0 && this.maxLimit < 0) ? this.minLimit : this.maxLimit;

    // Update text of limit counter
    this.limitSpan.textContent = `${count}/${limitValue}`;

    if (isMinFailed || isMaxFailed) {
      // Decide error texts based on current limitting method
      const errorPrefix = this.limitType === 'Words' ? 'word' : 'character';
      const errorTextKey = isMinFailed ? `${errorPrefix}MinLimitError` : `${errorPrefix}LimitError`;

      // Construct error text
      const errorText = `${global.JotForm.texts[errorTextKey] || FORM_TEXTS[errorTextKey]} ${limitValue}`;

      // Clear error message first
      if (this.editorInput.errorMessage !== errorText) {
        // Mark the field as corrected for clearing error statements
        global.JotForm.corrected(this.editorInput);
      }

      // Mark the field as errored
      global.JotForm.errored(this.editorInput, errorText);

      return errorText;
    }

    // Mark the field as corrected for clearing error statements
    global.JotForm.corrected(this.editorInput);

    return false;
  }

  setMarkdownFromHtml(html) {
    if (typeof global.toMarkdown === 'function') {
      // Use editor container value as fallback
      const htmlText = html !== undefined ? html : this.editorContainer.innerHTML;

      // Convert and set
      const markdownText = global.toMarkdown(htmlText);
      this.editorInput.value = markdownText.length !== 0 ? `jotform_untitled_markdown${markdownText}` : '';
    }
  }

  setHtmlFromMarkdown(md) {
    if (typeof global.showdown === 'object' && typeof global.showdown.Converter === 'function') {
      // Use hidden textarea value as fallback
      const mdText = md !== undefined ? md : this.editorInput.value.replace('jotform_untitled_markdown', '');

      // Convert and set
      const converter = new global.showdown.Converter();
      this.editorContainer.innerHTML = converter.makeHtml(mdText);
    }
  }

  initEditor() {
    if (this.editorInput.classList.contains('form-readonly')) {
      this.editorContainer.contentEditable = false;
      return;
    }

    // Set default value to editor container too
    this.setHtmlFromMarkdown(this.editorInput.value);

    // Bind editor toolbar buttons
    forEach(this.editorToolbarButtons, this.bindEditorButton);

    // Update toolbar status on editor manipulation
    forEach(this.editorManipulationEventList, eventType => this.editorContainer.addEventListener(eventType, this.handleEditorMutation));
  }
}

export default Textarea;
